"use client";

import Image from "next/image";
import { getStrapiMedia } from "../lib/api-helpers";

interface Picture {
  data: {
    id: string;
    attributes: {
      url: string;
      name: string;
      alternativeText: string;
    };
  };
}

interface ListOfFactors {
  id: string;
  label: string;
  description: string;
}

interface ThreePartSectionProps {
  data: {
    id: string;
    Title: string;
    Description: string;
    Image: Picture;
    BackgroundColor?: string;
    secondaryDescription?: ListOfFactors[];
    conclusion?: string;
  };
}

export default function ThreePartSection({ data }: ThreePartSectionProps) {
  const imgUrl = getStrapiMedia(data?.Image?.data?.attributes?.url);
  const backgroundColor = data.BackgroundColor || "#99ADFC1A";

  return (
    <section
      className="flex flex-col text-[#000000] body-font py-12 md:py-16 lg:py-20"
      aria-labelledby={`section-${data.id}`}
      style={{ backgroundColor: backgroundColor }}
    >
      <h2 id={`section-${data.id}`} className="sr-only">
        {data.Title || "Three Part Section"}
      </h2>

      <div className="container mx-auto xl:px-0 px-6">
        {/* Key: use items-end to align bottoms, gap for spacing */}
        <div className="flex flex-col lg:flex-row items-end gap-8 lg:gap-16 relative">
          
          {/* Text Content */}
          <article className="w-full lg:flex-[2.5] flex flex-col">
            {data.Title && (
              <div className="mb-6">
                <h1
                  className="font-bold text-3xl md:text-4xl lg:text-5xl leading-tight mb-0"
                  style={{ color: "#032D60" }}
                  dangerouslySetInnerHTML={{ __html: data.Title }}
                />
              </div>
            )}

            {data.Description && (
              <div
                className="text-[#000000] [&>p]:mb-3 text-base md:text-lg lg:text-xl leading-relaxed"
                dangerouslySetInnerHTML={{ __html: data.Description }}
              />
            )}
          </article>

          {/* Image */}
          <div className="w-full lg:flex-[1] flex-shrink-0 relative">
            {imgUrl ? (
              <div
                className="relative w-full max-w-[500px] mx-auto lg:mx-0"
                style={{ alignSelf: "flex-end" }}
              >
                <Image
                  src={imgUrl}
                  alt={data?.Image?.data?.attributes?.alternativeText || "none provided"}
                  className="object-contain object-bottom w-full h-auto"
                  width={600}
                  height={600}
                  priority={false}
                />
              </div>
            ) : (
              <div className="w-full h-64 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">
                No image available
              </div>
            )}
          </div>
        </div>
        {/* Secondary Description - List of Factors in 3x2 Grid */}
        {data.secondaryDescription && data.secondaryDescription.length > 0 && (
          <div className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.secondaryDescription.map((factor) => (
                <div key={factor.id} className="rounded-lg">
                  {factor.label && (
                    <h3 className="font-semibold text-lg text-[#032D60] mb-3">
                      {factor.label}
                    </h3>
                  )}
                  {factor.description && (
                    <p className="text-[#000000] text-base leading-relaxed">
                      {factor.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Conclusion - Optional */}
        {data.conclusion && (
          <div className="mt-8">
            <p className="text-[#000000] text-base leading-relaxed">
              {data.conclusion}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

